import React, { useState, useEffect, useRef } from 'react';
import { Menu } from 'lucide-react';
import { SearchBox } from './search/SearchBox';
import { useIsMobile } from '@/hooks/use-mobile';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import LanguageToggle from '@/components/ui/language-toggle';
import { Button } from '@/components/ui/button';

const MenuBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, signOut } = useAuth();
  const { isAdmin, isModerator } = useUserRole();

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current && 
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to handle menu item click and close menu
  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  // Menu sections data
  const publicMenuItems: Array<{ path: string; label: string }> = [
    { path: '/', label: t('navigation.home') },
    { path: '/ama', label: t('navigation.ama') },
    { path: '/how-to-use', label: t('navigation.howToUse') },
    { path: '/about', label: t('navigation.about') }
  ];

  const editorialMenuItems: Array<{ path: string; label: string }> = [
    { path: '/production', label: 'Produksjon' },
    { path: '/interview-requests', label: 'Intervjuforespørsler' },
    { path: '/interview-management', label: 'Intervjuadministrasjon' },
    { path: '/editorial/ama', label: 'AMA redaksjon' },
    { path: '/feeder', label: 'Publiseringskø' },
    { path: '/publish-history', label: 'Publiseringshistorikk' },
    { path: '/email-templates', label: 'E-post maler' },
    { path: '/ai-dashboard', label: 'AI Dashboard' },
    { path: '/editorial-approval', label: 'Redaksjonell godkjenning' },
    { path: '/gemini-test', label: 'Gemini Test' },
  ];

  const enterpriseMenuItems: Array<{ path: string; label: string }> = [
    { path: '/enterprise', label: '🚀 Enterprise Dashboard' },
    { path: '/enterprise/ad-economy', label: 'Annonseøkonomi' },
    { path: '/booking', label: 'Booking (BB)' },
    { path: '/analytics', label: 'Analytikk & Innsikt' },
    { path: '/ab-testing', label: 'A/B Testing' },
    { path: '/alerts', label: 'Overvåkning & Varsler' },
    { path: '/backup', label: 'Sikkerhetskopi' },
    { path: '/monitoring', label: 'Systemovervåkning' },
  ];

  const isAdminUser = isAuthenticated && (isAdmin || isModerator);

  return (
    <div className="w-full bg-white shadow-sm mb-8">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between py-4">
        {/* Menu on the left */}
        <div className="flex items-center">
          <div ref={menuButtonRef} className="cursor-pointer" onClick={toggleMenu}>
            <Menu className="w-5 h-5 md:w-6 md:h-6 text-[#ea384c]" />
          </div>
          
          {isMenuOpen && (
            <div 
              ref={menuRef} 
              className={`absolute top-16 left-4 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden max-h-[80vh] overflow-y-auto ${
                isAdminUser ? 'w-[600px]' : 'min-w-[200px]'
              }`}
            >
              {/* Authentication section at the top */}
              <div className="border-b border-gray-100 p-4">
                {isAuthenticated ? (
                  <div className="space-y-2">
                    {user?.email && (
                      <div className="text-sm text-muted-foreground">
                        {user.email}
                      </div>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        signOut();
                        handleMenuItemClick();
                      }}
                      className="text-sm w-full justify-start p-0 h-auto text-gray-700 hover:text-gray-900"
                    >
                      {t('auth.logOut')}
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    asChild
                    className="text-sm w-full justify-start p-0 h-auto text-gray-700 hover:text-gray-900"
                    onClick={handleMenuItemClick}
                  >
                    <Link to="/auth">{t('auth.logIn')}</Link>
                  </Button>
                )}
              </div>

              {/* Public menu items */}
              <div className="py-2">
                <ul className={isAdminUser ? 'grid grid-cols-2 gap-1 px-2' : ''}>
                  {publicMenuItems.map((item, index) => (
                    <li key={`public-item-${index}`}>
                      <Link 
                        to={item.path} 
                        className="block py-3 px-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                        onClick={handleMenuItemClick}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Editorial section - only for admin/moderator */}
              {isAdminUser && (
                <>
                  <div className="border-t border-gray-200">
                    <div className="py-2 px-4 text-sm text-center text-muted-foreground font-semibold bg-gray-50">
                      Redaksjon
                    </div>
                    <ul className="grid grid-cols-2 gap-1 px-2 py-2">
                      {editorialMenuItems.map((item, index) => (
                        <li key={`editorial-item-${index}`}>
                          <Link 
                            to={item.path} 
                            className="block py-3 px-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                            onClick={handleMenuItemClick}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Enterprise section */}
                  <div className="border-t border-gray-200">
                    <div className="py-2 px-4 text-sm text-center text-muted-foreground font-semibold bg-gray-50">
                      Enterprise
                    </div>
                    <ul className="grid grid-cols-2 gap-1 px-2 py-2">
                      {enterpriseMenuItems.map((item, index) => (
                        <li key={`enterprise-item-${index}`}>
                          <Link 
                            to={item.path} 
                            className="block py-3 px-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                            onClick={handleMenuItemClick}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Center logo */}
        <div className={`${isMobile ? "absolute left-1/2 -translate-x-1/2" : "absolute left-1/2 transform -translate-x-1/2"}`}>
          <Link to="/proto1" className="block">
            <img 
              src="/lovable-uploads/24339f27-7064-4bc1-a0ed-462456a998ec.png" 
              alt="Beefr Logo" 
              className="h-8 w-auto md:h-12"
            />
          </Link>
        </div>
        
        {/* Search and Language Toggle on the right */}
        <div className="flex items-center justify-end gap-2">
          <SearchBox alwaysShowInput={false} />
          <LanguageToggle />
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
